import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { DocCategoryService } from "../../services/docCategoryService";
import { DocCategoryDetail, DocCategoryFormData, DocCategoryListItem, defaultDocCategoryData, OperationStatus } from "./constants";
import { getErrorMessage } from "../../utils/errorUtils";
import { useMemo, useCallback } from "react";

interface DocCategoryContextState {
	// Core Domain Data
	docCategoryList: DocCategoryListItem[];
	docCategoryDetail: DocCategoryDetail | null;
	docCategoryForm: DocCategoryFormData;
	operationStatus: OperationStatus;

	// Setters
	setDocCategoryForm: React.Dispatch<React.SetStateAction<DocCategoryFormData>>;
	clearDocCategoryData: () => void;
    clearDocCategoryList: () => void;

	// Actions
	addDocCategory: () => Promise<void>;
	getDocCategoryList: () => Promise<void>;
	updateDocCategory: () => Promise<void>;
	removeDocCategory: (id: string) => Promise<void>;
	getDocCategoryDetails: (id: string) => Promise<void>;
}

interface DocCategoryProviderProps {
	children: React.ReactNode;
}

const DocCategoryContext = createContext<DocCategoryContextState | undefined>(
	undefined
);

export const useDocCategoryContext = () => {
	const context = useContext(DocCategoryContext);
	if (!context) {
		throw new Error(
			"useDocumentCategoryContext must be used within an DocumentCategoryContextProvider"
		);
	}
	return context;
};

export const DocCategoryContextProvider: React.FC<DocCategoryProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [docCategoryList, setDocCategoryList] = useState<DocCategoryListItem[]>([]);
	const [docCategoryDetail, setDocCategoryDetail] = useState<DocCategoryDetail | null>(null);
	const [docCategoryForm, setDocCategoryForm] = useState<DocCategoryFormData>(defaultDocCategoryData);

	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const clearDocCategoryData = () => {
		setDocCategoryForm(defaultDocCategoryData);
		setDocCategoryDetail(null);
	};

	const clearDocCategoryList = () => {
		setDocCategoryList([]);
	};

	const getDocCategoryList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await DocCategoryService.getDocCategoryList();

			if (response.isSuccessful) {
				setDocCategoryList(response.result?.items || []);
				clearDocCategoryData();
			} else {
				message.error(response.error ? response.error : "Belge kategorisi listesi yüklenirken hata oluştu.");
				setDocCategoryList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [clearDocCategoryData]);

	const addDocCategory = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!docCategoryForm.categoryName?.trim()) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await DocCategoryService.addDocCategory(docCategoryForm);

			if (response.isSuccessful) {
				message.success("Belge kategorisi başarıyla eklendi.");
				setDocCategoryForm(defaultDocCategoryData);
				navigate("/doc-categories");
			} else {
				message.error(response.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [docCategoryForm, navigate]);

	const getDocCategoryDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });
		
		try {
			const response = await DocCategoryService.getDocCategoryDetails(id);

			if (response.isSuccessful) {
				setDocCategoryDetail({
					categoryId: response.result.categoryId,
					categoryNo: response.result.categoryNo,
					categoryName: response.result.categoryName,
				});

				setDocCategoryForm({
					...docCategoryForm,
					categoryId: response.result.categoryId
				});
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const removeDocCategory = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await DocCategoryService.removeDocCategory(id);
			if (response.isSuccessful) {
				message.success("Belge kategorisi başarıyla silindi.");
				await getDocCategoryList();
			} else {
				message.error(response.error?.message || "Belge kategorisi silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [getDocCategoryList]);

	const updateDocCategory = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!docCategoryForm.categoryId) {
			message.info("Kategori numarası bulunamadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const isCategoryNameChanged = docCategoryForm.categoryName?.trim() && docCategoryForm.categoryName?.trim() !== docCategoryDetail?.categoryName;
		const isDisplayOrderChanged = docCategoryForm.categoryNo !== undefined && docCategoryForm.categoryNo !== docCategoryDetail?.categoryNo;

		if (!isCategoryNameChanged && !isDisplayOrderChanged) {
			message.info("Bir değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await DocCategoryService.updateDocCategory(docCategoryForm);

			if (response.isSuccessful) {
				message.success("Belge kategorisi başarıyla güncellendi.");
				clearDocCategoryData();
				navigate("/doc-categories");
			} else {
				message.error(response.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [docCategoryForm,
		docCategoryDetail,
		navigate,
		clearDocCategoryData]);

	const contextValue = useMemo<DocCategoryContextState>(
		() => ({
			docCategoryForm,
			docCategoryList,
			docCategoryDetail,
			setDocCategoryForm,
			operationStatus,
			addDocCategory,
			removeDocCategory,
			updateDocCategory,
			getDocCategoryList,
			getDocCategoryDetails,
			clearDocCategoryData,
			clearDocCategoryList
		}),
		[
			docCategoryForm,
			docCategoryList,
			docCategoryDetail,
			setDocCategoryForm,
			operationStatus,
			addDocCategory,
			removeDocCategory,
			updateDocCategory,
			getDocCategoryList,
			getDocCategoryDetails,
			clearDocCategoryData,
			clearDocCategoryList
		]
	);

	return (
		<DocCategoryContext.Provider value={contextValue}>
			{children}
		</DocCategoryContext.Provider>
	);
};